/* BodyHub — 5 body routines in one hub: hair, body/nail, dental, scent, diet.
 * Each has editable daily checklist + streak tracking. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check, Plus, Edit2, X, Save, Flame, Trash2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHEN_OPTIONS = [
  { v: 'morgon', label: '🌅 morgon' },
  { v: 'lunch', label: '☀️ lunch' },
  { v: 'eftermiddag', label: '🌤 eftermiddag' },
  { v: 'kväll', label: '🌙 kväll' },
  { v: 'morgon+kväll', label: '☀️ morgon + kväll' },
  { v: 'duschtid', label: '🚿 duschtid' },
  { v: 'hela dagen', label: '⏰ hela dagen' },
];

// Static Tailwind class maps per accent (Tailwind can't purge dynamic template literals)
const ACCENT_CLASSES = {
  amber:   { border: 'border-amber-200',   textStrong: 'text-amber-800', text900: 'text-amber-900',   textSoft: 'text-amber-800/80', textSoft80: 'text-amber-900/80', btn: 'bg-amber-500 hover:bg-amber-600', tileFrom: 'from-amber-400', tileTo: 'to-amber-500', icon: 'text-amber-500' },
  rose:    { border: 'border-rose-200',    textStrong: 'text-rose-800',  text900: 'text-rose-900',    textSoft: 'text-rose-800/80',  textSoft80: 'text-rose-900/80',  btn: 'bg-rose-500 hover:bg-rose-600',  tileFrom: 'from-rose-400',  tileTo: 'to-rose-500',  icon: 'text-rose-500' },
  sky:     { border: 'border-sky-200',     textStrong: 'text-sky-800',   text900: 'text-sky-900',     textSoft: 'text-sky-800/80',   textSoft80: 'text-sky-900/80',   btn: 'bg-sky-500 hover:bg-sky-600',    tileFrom: 'from-sky-400',   tileTo: 'to-sky-500',   icon: 'text-sky-500' },
  fuchsia: { border: 'border-fuchsia-200', textStrong: 'text-fuchsia-800', text900: 'text-fuchsia-900', textSoft: 'text-fuchsia-800/80', textSoft80: 'text-fuchsia-900/80', btn: 'bg-fuchsia-500 hover:bg-fuchsia-600', tileFrom: 'from-fuchsia-400', tileTo: 'to-fuchsia-500', icon: 'text-fuchsia-500' },
  emerald: { border: 'border-emerald-200', textStrong: 'text-emerald-800', text900: 'text-emerald-900', textSoft: 'text-emerald-800/80', textSoft80: 'text-emerald-900/80', btn: 'bg-emerald-500 hover:bg-emerald-600', tileFrom: 'from-emerald-400', tileTo: 'to-emerald-500', icon: 'text-emerald-500' },
};
const cx = (accent) => ACCENT_CLASSES[accent] || ACCENT_CLASSES.rose;

export default function BodyHub() {
  const [meta, setMeta] = useState([]);
  const [active, setActive] = useState('hair');
  const [today, setToday] = useState(null);
  const [streak, setStreak] = useState({ streak: 0, days: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // item id being edited
  const [form, setForm] = useState({ step: '', desc: '', when: 'morgon' });

  const load = useCallback(async (routine) => {
    setLoading(true);
    try {
      const [{ data: t }, { data: s }] = await Promise.all([
        axios.get(`${API}/body-hub/${routine}/today`, { withCredentials: true }),
        axios.get(`${API}/body-hub/${routine}/streak?days=14`, { withCredentials: true }),
      ]);
      setToday(t);
      setStreak(s);
    } catch { toast.error('Kunde inte ladda rutinen'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    axios.get(`${API}/body-hub/meta`, { withCredentials: true })
      .then(({ data }) => setMeta(data.routines || []))
      .catch(() => {});
  }, []);

  useEffect(() => { load(active); }, [active, load]);

  const toggle = async (itemId, isDone) => {
    try {
      if (isDone) {
        await axios.delete(`${API}/body-hub/${active}/complete/${itemId}`, { withCredentials: true });
      } else {
        await axios.post(`${API}/body-hub/${active}/complete`, { item_id: itemId }, { withCredentials: true });
      }
      await load(active);
    } catch { toast.error('Kunde inte spara'); }
  };

  const addStep = async () => {
    if (form.step.trim().length < 2) { toast.error('Skriv namn på steget'); return; }
    try {
      await axios.post(`${API}/body-hub/${active}/items`, form, { withCredentials: true });
      setForm({ step: '', desc: '', when: 'morgon' });
      setAdding(false);
      toast.success('Tillagt 💜');
      await load(active);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte spara'); }
  };

  const patchStep = async (itemId, updates) => {
    try {
      await axios.patch(`${API}/body-hub/${active}/items/${itemId}`, updates, { withCredentials: true });
      setEditing(null);
      toast.success('Sparat 💜');
      await load(active);
    } catch { toast.error('Kunde inte spara'); }
  };

  const delStep = async (itemId) => {
    if (!window.confirm('Ta bort detta steg?')) return;
    try {
      await axios.delete(`${API}/body-hub/${active}/items/${itemId}`, { withCredentials: true });
      await load(active);
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const m = meta.find((r) => r.id === active);
  const cs = cx(m?.accent);
  const doneSet = new Set(today?.completed || []);

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="body-hub-page">
      {/* Routine tabs */}
      <div className="flex flex-wrap gap-1.5" data-testid="body-hub-tabs">
        {meta.map((r) => {
          const acs = cx(r.accent);
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => setActive(r.id)}
              data-testid={`body-hub-tab-${r.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition ${
                active === r.id
                  ? `bg-gradient-to-br ${r.gradient} ${acs.border} text-slate-900 shadow-sm`
                  : 'bg-white border-transparent opacity-70 text-slate-600 hover:opacity-100'
              }`}
            >
              {r.emoji} {r.label}
            </button>
          );
        })}
      </div>

      {loading || !today || !m ? (
        <div className="text-center py-12 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Laddar…
        </div>
      ) : (
        <>
          {/* Hero */}
          <div className={`rounded-3xl p-5 bg-gradient-to-br ${m.gradient} border ${cs.border} relative overflow-hidden`}>
            <div className="absolute -right-4 -top-4 opacity-15 text-8xl leading-none pointer-events-none">{m.emoji}</div>
            <div className={`text-[11px] uppercase font-semibold tracking-wider ${cs.textSoft}`}>
              {m.label}
            </div>
            <h1 className={`text-2xl font-bold ${cs.text900}`} style={{ fontFamily: 'ui-serif, Georgia, serif' }}>
              {today.items.filter((i) => doneSet.has(i.id)).length} / {today.items.length} klara idag
            </h1>
            <p className={`text-sm ${cs.textSoft80} mt-2 italic`}>"{m.quote}"</p>
            {streak.streak > 0 && (
              <div className="mt-2">
                <span className="text-[10px] rounded-full px-1.5 py-0.5 border bg-white/60 border-amber-200 text-amber-800 inline-flex items-center gap-0.5">
                  <Flame className="w-2.5 h-2.5" /> {streak.streak} dagar streak
                </span>
              </div>
            )}
          </div>

          {/* Today's steps */}
          <div className="rounded-2xl bg-white border border-slate-100 p-4">
            <div className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5">
              <Sparkles className={`w-3.5 h-3.5 ${cs.icon}`} /> Idag
            </div>
            {today.items.length === 0 && (
              <div className="text-xs text-slate-400 italic text-center py-3">
                Inga steg än. Lägg till ditt första steg nedan.
              </div>
            )}
            <div className="space-y-1.5">
              {today.items.map((s) => {
                const done = doneSet.has(s.id);
                const isEditing = editing === s.id;
                if (isEditing) {
                  return (
                    <InlineEditor
                      key={s.id}
                      initial={s}
                      onCancel={() => setEditing(null)}
                      onSave={(u) => patchStep(s.id, u)}
                    />
                  );
                }
                return (
                  <div
                    key={s.id}
                    className={`flex items-start gap-2 p-2 rounded-lg group ${done ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
                    data-testid={`body-hub-item-${s.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(s.id, done)}
                      data-testid={`body-hub-toggle-${s.id}`}
                      className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 bg-white'
                      }`}
                      aria-label={done ? 'Ångra' : 'Klart'}
                    >
                      {done && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium ${done ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{s.step}</div>
                      {s.desc && <div className="text-[11px] text-slate-500 mt-0.5">{s.desc}</div>}
                    </div>
                    <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-slate-50 border border-slate-200 text-slate-600 flex-shrink-0">
                      {s.when}
                    </span>
                    <div className="opacity-0 group-hover:opacity-100 flex gap-1 flex-shrink-0">
                      <button onClick={() => setEditing(s.id)} className="text-slate-400 hover:text-slate-700" aria-label="Redigera">
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button onClick={() => delStep(s.id)} className="text-slate-400 hover:text-red-500" aria-label="Ta bort">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {adding ? (
              <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-2">
                <input
                  type="text"
                  value={form.step}
                  onChange={(e) => setForm({ ...form, step: e.target.value })}
                  placeholder="Steg-namn (t.ex. Torrborsta)"
                  data-testid="body-hub-add-step"
                  className="w-full text-sm rounded-lg border border-slate-200 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-rose-200"
                  autoFocus
                />
                <input
                  type="text"
                  value={form.desc}
                  onChange={(e) => setForm({ ...form, desc: e.target.value })}
                  placeholder="Kort instruktion (valfritt)"
                  className="w-full text-xs rounded-lg border border-slate-200 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-rose-200"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={form.when}
                    onChange={(e) => setForm({ ...form, when: e.target.value })}
                    className="text-xs rounded-lg border border-slate-200 px-2 py-1"
                  >
                    {WHEN_OPTIONS.map((w) => <option key={w.v} value={w.v}>{w.label}</option>)}
                  </select>
                  <div className="flex-1" />
                  <button
                    onClick={() => { setAdding(false); setForm({ step: '', desc: '', when: 'morgon' }); }}
                    className="text-xs px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-600"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={addStep}
                    data-testid="body-hub-save-step"
                    className={`text-xs px-3 py-1 rounded-full ${cs.btn} text-white font-semibold`}
                  >
                    Spara
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAdding(true)}
                data-testid="body-hub-add-btn"
                className="w-full mt-3 text-xs px-3 py-2 rounded-xl bg-slate-50 hover:bg-slate-100 border border-dashed border-slate-300 text-slate-600 inline-flex items-center justify-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" /> Lägg till steg
              </button>
            )}
          </div>

          {/* Streak strip */}
          <div className="rounded-2xl bg-gradient-to-r from-amber-50 to-rose-50 border border-amber-200 p-3">
            <div className="text-xs font-semibold text-amber-900 mb-2 flex items-center gap-1">
              <Flame className="w-3 h-3" /> Streak — {streak.streak} dagar i rad
            </div>
            <div className="flex gap-1">
              {streak.days.map((d) => {
                const c = streak.counts[d] || 0;
                const dt = new Date(d + 'T00:00:00');
                const wd = ['sö', 'må', 'ti', 'on', 'to', 'fr', 'lö'][dt.getDay()];
                return (
                  <div key={d} className="flex-1 flex flex-col items-center gap-0.5">
                    <div className={`w-full aspect-square rounded ${c > 0 ? `bg-gradient-to-br ${cs.tileFrom} ${cs.tileTo}` : 'bg-white border border-slate-200'}`} title={d} />
                    <div className="text-[9px] text-slate-500">{wd}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function InlineEditor({ initial, onCancel, onSave }) {
  const [step, setStep] = useState(initial.step);
  const [desc, setDesc] = useState(initial.desc || '');
  const [when, setWhen] = useState(initial.when || 'morgon');
  return (
    <div className="rounded-lg bg-rose-50 border border-rose-200 p-2 space-y-1.5" data-testid={`inline-editor-${initial.id}`}>
      <input value={step} onChange={(e) => setStep(e.target.value)} className="w-full text-sm rounded border border-slate-200 px-2 py-1" />
      <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Beskrivning" className="w-full text-xs rounded border border-slate-200 px-2 py-1" />
      <div className="flex items-center gap-1.5">
        <select value={when} onChange={(e) => setWhen(e.target.value)} className="text-xs rounded border border-slate-200 px-1 py-0.5">
          {WHEN_OPTIONS.map((w) => <option key={w.v} value={w.v}>{w.label}</option>)}
        </select>
        <div className="flex-1" />
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-700" aria-label="Avbryt"><X className="w-3.5 h-3.5" /></button>
        <button onClick={() => onSave({ step, desc, when })} className="text-emerald-600 hover:text-emerald-700" aria-label="Spara"><Save className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}
